import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn, initialsOf } from "@/lib/utils";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const sportLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "Football");

export default function AthleteSelector({ onNavigate }) {
  const { t, tf } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data || [])).catch(() => {});
  }, []);

  const selectedId = location.pathname === "/dashboard" ? searchParams.get("player") : null;
  const selected = useMemo(() => players.find((p) => p.id === selectedId) || null, [players, selectedId]);

  const goDashboard = (playerId) => {
    const p = new URLSearchParams(location.pathname === "/dashboard" ? searchParams : undefined);
    if (playerId) p.set("player", playerId); else p.delete("player");
    const qs = p.toString();
    navigate(`/dashboard${qs ? `?${qs}` : ""}`);
    setOpen(false);
    onNavigate?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={t("sidebar.allAthletes")}
          data-testid="portfolio-selector"
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border border-border bg-secondary/50 px-3 text-xs",
            "hover:bg-secondary/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          )}
        >
          {selected ? (
            selected.photo_url ? (
              <img src={selected.photo_url} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-athlynx/15 border border-athlynx/30 text-athlynx text-[9px] font-bold flex items-center justify-center shrink-0">
                {initialsOf(selected.name)}
              </span>
            )
          ) : (
            <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="flex-1 truncate text-left font-medium" data-testid="portfolio-selector-value">
            {selected ? selected.name : t("sidebar.allAthletes")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={tf("Rechercher un athlète…")} data-testid="athlete-selector-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>{tf("Aucun athlète trouvé")}</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" data-testid="portfolio-option-all" onSelect={() => goDashboard(null)}>
                <Users className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                <span className="flex-1">{t("sidebar.allAthletes")}</span>
                {!selectedId && <Check className="h-3.5 w-3.5 text-athlynx" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup>
              {players.map((p) => (
                <CommandItem key={p.id} value={`${p.name} ${p.club || ""} ${p.sport || ""}`}
                  data-testid={`portfolio-option-${p.id}`} onSelect={() => goDashboard(p.id)}>
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="h-6 w-6 rounded-full object-cover mr-2 shrink-0" />
                  ) : (
                    <span className="h-6 w-6 rounded-full bg-secondary border border-border text-[9px] font-bold flex items-center justify-center mr-2 shrink-0">
                      {initialsOf(p.name)}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">{p.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {sportLabel(p.sport)}{p.club ? ` · ${p.club}` : ""}
                    </span>
                  </span>
                  {selectedId === p.id && <Check className="h-3.5 w-3.5 text-athlynx shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
